import { AlarmClock, Target, Mic, MessagesSquare, FileText, ShieldCheck } from 'lucide-react';
import { Puce, Section, TEINTES } from './constantes';

const Epreuve = ({ e, couleur }) => {
  const t = TEINTES[couleur];
  return (
    <div className={`rounded-2xl border-t-4 bg-white p-5 shadow-sm ${t.bord}`} data-testid={`guide-epreuve-${e.bloc}`}>
      <p className={`text-xs font-bold uppercase tracking-[0.2em] ${t.texte}`}>{e.bloc}</p>
      <h3 className="mt-1 text-lg font-bold text-[#0F172A]">{e.titre}</h3>
      <ul className="mt-4 space-y-3 text-sm text-slate-700">
        <li className="flex gap-2.5"><FileText className={`mt-0.5 h-4 w-4 shrink-0 ${t.texte}`} />{e.production}</li>
        <li className="flex gap-2.5"><AlarmClock className={`mt-0.5 h-4 w-4 shrink-0 ${t.texte}`} />{e.remise}</li>
        <li className="flex gap-2.5"><Mic className={`mt-0.5 h-4 w-4 shrink-0 ${t.texte}`} />{e.oral}</li>
        <li className="flex gap-2.5"><MessagesSquare className={`mt-0.5 h-4 w-4 shrink-0 ${t.texte}`} />{e.echange}</li>
      </ul>
    </div>
  );
};

const Sommaire = ({ titre, lignes, testId }) => (
  <div data-testid={testId}>
    <h3 className="mb-2 text-sm font-bold text-[#0F172A]">{titre}</h3>
    <ol className="divide-y divide-slate-100 rounded-xl border border-slate-200">
      {lignes.map(([p, c, n]) => (
        <li key={p} className="flex items-start gap-3 px-3 py-2.5 text-sm">
          <span className="w-44 shrink-0 font-semibold text-[#0F172A] sm:w-56">{p}</span><span className="flex-1 text-slate-600">{c}</span><span className="text-xs text-slate-400">{n}</span>
        </li>
      ))}
    </ol>
  </div>
);

const PageGarde = ({ elements }) => (
  <div className="grid gap-5 md:grid-cols-[1fr_260px]">
    <ul className="space-y-2">{elements.map((x) => <Puce key={x}>{x}</Puce>)}</ul>
    <div className="flex aspect-[1/1.41] flex-col items-center justify-between rounded-lg border border-slate-200 bg-slate-50 p-5 text-center shadow-inner" aria-hidden>
      <p className="text-[9px] uppercase tracking-widest text-slate-400">BPJEPS · Motonautisme · BC1</p>
      <div><p className="text-lg font-bold text-[#0F172A]">Les Mercredis Glisse & Océan</p><div className="mx-auto mt-2 h-0.5 w-16 bg-[#0E7C74]" /></div>
      <div className="flex h-16 w-full items-center justify-center rounded border border-dashed border-slate-300 text-[10px] text-slate-400">[Logo de la structure]</div>
      <p className="text-[9px] italic text-slate-500">Présenté par [Prénom NOM] · Tuteur : [Nom] · FISL · Session 2026</p>
    </div>
  </div>
);

export const OngletMethode = ({ guide }) => {
  const m = guide.methode;
  const s = guide.structure;
  return (
    <div className="space-y-6" data-testid="guide-methode">
      <Section titre="Méthodologie" sousTitre={m.intro}>
        <div className="grid gap-4 md:grid-cols-2">{m.epreuves.map((e, k) => <Epreuve key={e.bloc} e={e} couleur={guide.blocs[k].couleur} />)}</div>
        <div className="mt-5 rounded-2xl border-2 border-red-200 bg-red-50 p-4" data-testid="guide-dates">
          <p className="flex items-center gap-2 font-bold text-red-700"><AlarmClock className="h-5 w-5" /> Dates de remise</p>
          <ul className="mt-2 space-y-1 text-sm text-red-800">{m.dates.map((d) => <li key={d}>{d}</li>)}</ul>
        </div>
      </Section>
      <Section titre="Ordre conseillé" testId="guide-ordre">
        <ol className="grid gap-3 md:grid-cols-3">
          {m.ordre.map((o, k) => (
            <li key={o.titre} className="rounded-2xl bg-slate-50 p-4">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#0F172A] font-bold text-white">{k + 1}</span>
              <p className="mt-3 font-semibold text-[#0F172A]">{o.titre}</p><p className="mt-1 text-sm text-slate-600">{o.texte}</p>
            </li>
          ))}
        </ol>
        <p className="mt-4 flex gap-2.5 rounded-xl border-l-4 border-[#E9A23B] bg-[#FFF8E6] p-4 text-sm text-[#7A4B00]"><Target className="mt-0.5 h-4 w-4 shrink-0" /><span><b>Niveau 4 : </b>{m.niveau4}</span></p>
      </Section>
      <Section titre="Page de garde : éléments obligatoires" testId="guide-page-garde"><PageGarde elements={m.page_garde} /></Section>
      <Section titre="Organiser ses documents" testId="guide-sommaires">
        <div className="space-y-5">
          <Sommaire titre="Sommaire conseillé du dossier BC1 (25 pages maximum)" lignes={m.sommaire_bc1} testId="guide-sommaire-bc1" />
          <Sommaire titre="Plan conseillé des supports du BC2" lignes={m.sommaire_bc2} testId="guide-sommaire-bc2" />
        </div>
      </Section>
      <Section titre="Les règles d'or" testId="guide-regles">
        <ol className="space-y-2.5">{m.regles.map((r, k) => <li key={r} className="flex gap-3 text-sm text-slate-700"><span className="font-bold text-[#0E7C74]">{k + 1}.</span>{r}</li>)}</ol>
        <div className="mt-5 rounded-xl border-l-4 border-[#8DC63F] bg-[#8DC63F]/10 p-4" data-testid="guide-regles-fisl">
          <p className="flex items-center gap-2 text-sm font-bold text-[#0F172A]"><ShieldCheck className="h-4 w-4 text-[#6FA22E]" /> Règles FISL</p>
          <ul className="mt-2 space-y-1 text-sm text-slate-700">{m.regles_fisl.map((x) => <li key={x}>{x}</li>)}</ul>
        </div>
      </Section>
      <Section titre={`La structure des exemples : « ${s.nom} »`} sousTitre={s.description} testId="guide-structure">
        <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">{s.fiche.map(([k, v]) => <div key={k}><dt className="text-xs font-bold uppercase tracking-wide text-slate-400">{k}</dt><dd className="mt-0.5 text-sm text-slate-700">{v}</dd></div>)}</dl>
        <div className="mt-5 grid gap-3 md:grid-cols-2">
          <p className="rounded-xl bg-[#E6F4F2] p-4 text-sm text-[#0E5F59]"><b>Fil rouge du BC1 : </b>{s.fil_rouge_bc1}</p>
          <div className="rounded-xl bg-[#FDECE8] p-4 text-sm text-[#9A3412]"><b>Les 2 actions du BC2 :</b>{s.actions_bc2.map((a) => <p key={a} className="mt-1">{a}</p>)}</div>
        </div>
      </Section>
    </div>
  );
};
